import { gsap } from 'gsap/dist/gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import { useEffect, useRef, useState, type FC } from 'react'
import styled from 'styled-components'

import type { AcademyArticle } from '../AcademyContentPage'

export type AnchorRecord = {
  __typename: string
  id: string
  navigationTitle: string
  sectionId: string
}
const StyledArticleNavigationWrap = styled.div`
  max-width: 1150px;
  width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 100;
  @media screen and (max-width: 990px) {
    display: none;
  }
`

const StyledArticleNavigation = styled.div`
  z-index: 100;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  width: 200px;
  padding: 70px 30px 20px;
  position: absolute;
  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    li {
      padding: 5px 0;
      a {
        color: ${(props) => props.theme.v2.text.secondary};
        font-size: 16px;
        line-height: 35px;
        position: relative;
        padding-left: 10px;
        display: block;
        &:before {
          content: '';
          position: absolute;
          left: -5px;
          top: 15px;
          width: 5px;
          height: 5px;
          border-radius: 5px;
          background-color: ${(props) => props.theme.v2.text.secondary};
        }
        &.active {
          font-weight: 500;
          color: ${(props) => props.theme.v2.interactive.bright};
          &:before {
            background-color: ${(props) => props.theme.v2.interactive.bright};
          }
        }
      }
    }
  }
`

export const ArticleNavigation: FC<{ article: AcademyArticle }> = ({ article }) => {
  const ref = useRef(null)
  gsap.registerPlugin(ScrollTrigger)
  const elements = createNavigationElementsList(createAnchorRecordsList(article.articleContent))
  const [activeHref, setActiveHref] = useState('')

  useEffect(() => {
    const articleElement = ref.current
    ScrollTrigger.create({
      trigger: '.articleContent',
      start: 'top top',
      end: 'bottom bottom',
      markers: false,
      pin: articleElement,
    })
  }, [])

  return elements ? (
    <StyledArticleNavigationWrap>
      <StyledArticleNavigation ref={ref}>
        <ul>
          {elements.map((element) => {
            return (
              <li key={element.id}>
                <a
                  className={activeHref === element.id ? 'active' : ''}
                  onClick={() => setActiveHref(element.id)}
                  href={element.href}
                >
                  {element.title}
                </a>
              </li>
            )
          })}
        </ul>
      </StyledArticleNavigation>
    </StyledArticleNavigationWrap>
  ) : (
    <div></div>
  )
}

export const createAnchorRecordsList = <TBlock extends { __typename: string }>(articleContent: {
  blocks: TBlock[]
}) => {
  return articleContent.blocks.filter(
    (block): block is Extract<TBlock, { __typename: 'AnchorRecord' }> => block.__typename === 'AnchorRecord',
  )
}

export const createNavigationElementsList = (anchorRecords: AnchorRecord[]) => {
  return anchorRecords.map((element) => {
    return {
      id: element.id,
      title: element.navigationTitle,
      href: `#${element.sectionId}`,
    }
  })
}
